import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

const STRETCH_TOLERANCE = 1.2;

function authoredSeconds(context: RuntimeLintContext): number | null {
    const { timeline } = context;
    if (!timeline || typeof timeline.duration !== 'function') {
        return null;
    }
    const duration = timeline.duration();
    return Number.isFinite(duration) && duration > 0 ? duration : null;
}

function trimNumber(value: number, places: number): string {
    return value.toFixed(places).replace(/\.?0+$/, '');
}

export const timelineDurationMismatch: RuntimeLintRule = {
    id: 'timeline-duration-mismatch',
    severity: 'error',
    guidance:
        'Author the timeline in real seconds and make it exactly as long as the scene. The host ' +
        'seeks by normalized progress, so a timeline shorter than the scene does not leave a hold ' +
        'at the end — it stretches every tween across the whole scene (2s of tweens in a 10s scene ' +
        'plays at a fifth speed). Close the timeline with a zero-duration spacer at the scene ' +
        'length — `tl.set({}, {}, SCENE_SECONDS)` — so the graphic keeps its authored pacing and ' +
        'then holds. Do NOT reach for `tl.duration(...)`: it rescales timeScale rather than ' +
        'lengthening the timeline, which changes nothing under progress-driven seeking.',
    check(context: RuntimeLintContext): string[] {
        const { sceneDurationMs } = context;
        if (
            typeof sceneDurationMs !== 'number' ||
            !Number.isFinite(sceneDurationMs) ||
            sceneDurationMs <= 0
        ) {
            return [];
        }
        const authored = authoredSeconds(context);
        if (authored === null) {
            return [];
        }

        const sceneSeconds = sceneDurationMs / 1000;
        const stretch = sceneSeconds / authored;
        if (stretch <= STRETCH_TOLERANCE && stretch >= 1 / STRETCH_TOLERANCE) {
            return [];
        }

        const speed = 1 / stretch;
        const authoredText = `${trimNumber(authored, 2)}s`;
        const sceneText = `${trimNumber(sceneSeconds, 2)}s`;
        const speedText = `${trimNumber(speed, 2)}x`;

        if (stretch > 1) {
            return [
                `The timeline runs ${authoredText} but the scene is ${sceneText}, so the host ` +
                    `stretches it across the scene and everything plays at ${speedText} speed. Pad ` +
                    `the timeline to the scene length with \`tl.set({}, {}, ${trimNumber(sceneSeconds, 2)})\` ` +
                    `as its last call, so the graphic keeps its pacing and holds after its last beat.`,
            ];
        }
        return [
            `The timeline runs ${authoredText} but the scene is only ${sceneText}, so the host ` +
                `compresses it and everything plays at ${speedText} speed. Shorten the timeline to ` +
                `the scene length, or lengthen the scene.`,
        ];
    },
};
